import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["accessLevel", "actions", "objectId", "objectTypeId", "property", "value", "enableConditionalPropertyOptions", "formControlProps", "getAdditionalPropertyValue", "getSavedValue", "getDraftValue"];
import { useEffect, useMemo, useContext } from 'react';
import { isFile } from 'customer-data-objects/property/PropertyIdentifier';
import { HIDDEN } from 'customer-data-objects/property/AccessLevel';
import { PropertyContextProvider } from '../context/PropertyContext/PropertyContextProvider';
import { ActionsContext } from '../context/ActionsContext';
import { ObjectContext } from '../context/ObjectContext';
import { usePropertyAccessLevel } from '../hooks/usePropertyAccessLevel';
import { usePropertyDefinition } from '../hooks/usePropertyDefinition';
import { EDIT_INPUT_MODE } from '../types/PropertyInputV2Component';
import { usePropertyValue } from '../hooks/usePropertyValue';
import { useActionsForProperty } from '../hooks/useActionsForProperty';
import { useObjectId } from '../hooks/useObjectId';
import { useObjectType } from '../hooks/useObjectType';
import { useObjectTypeId } from '../hooks/useObjectTypeId';
import { usePropertyInputComponent } from '../hooks/usePropertyInputComponent';
import emptyFunction from 'react-utils/emptyFunction';
import InputWithFloatingFormControlFocus from './InputWithFloatingFormControlFocus';
import { usePropertyValidation } from '../../validation/hooks/usePropertyValidation';
import FormControl from './FormControl';
import { useInternalPropertyValue } from '../hooks/useInternalPropertyValue';
import { Metrics } from '../../metrics/Metrics';
import devLogger from 'react-utils/devLogger';
import useConditionalPropertiesPanel from '../hooks/useConditionalPropertiesPanel';
import { useConditionalPropertyOptions } from '../hooks/useConditionalPropertyOptions';
import Raven from 'raven-js';
import { useHideFloatingEditIcon } from '../hooks/useHideFloatingEditIcon';
import useGetAdditionalPropertyValueProxy from '../hooks/useGetAdditionalPropertyValueProxy';
import { normalizePropertyDefinition } from '../../utils/normalizePropertyDefinition';
import { RequirablePropertyInputHighlySensitive } from '../../input/PropertyInputGetters';
import { useIsHidingHighlySensitiveValue } from '../../dataSensitivity/useIsHidingHighlySensitiveValue';
import { useIsPropertyReadOnly } from '../hooks/useIsPropertyReadOnly';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useValueCanary = ({
  objectTypeId,
  property,
  value
}) => {
  useEffect(() => {
    if (value !== null && value !== undefined && typeof value !== 'string') {
      devLogger.warn({
        message: `propertyinputv2-value-canary: received an invalid value type (must be string, null, or undefined): objectTypeId=${objectTypeId}, propertyName=${property.name}, valueType=${typeof value}`,
        key: 'propertyinputv2-value-canary'
      });
      Metrics.counter('propertyinputv2-value-canary').increment();
      Raven.captureException(new Error('propertyinputv2 value canary'), {
        extra: {
          objectTypeId,
          propertyName: property.name,
          valueType: typeof value
        }
      });
    }
  }, [objectTypeId, property.name, value]);
};
const useObjectIdCanary = ({
  objectTypeId,
  objectId,
  inputMode = EDIT_INPUT_MODE,
  property
}) => {
  useEffect(() => {
    if (inputMode === EDIT_INPUT_MODE && !objectId) {
      devLogger.warn({
        message: `propertyinputv2-objectid-canary: received an invalid objectId/inputMode (objectId is required when inputMode = "edit"): objectTypeId=${objectTypeId}, objectId=${objectId}, inputMode=${inputMode}, propertyName=${property.name}`,
        key: 'propertyinputv2-objectid-canary'
      });
      Metrics.counter('propertyinputv2-objectid-canary').increment();
      Raven.captureException(new Error('propertyinputv2 objectid canary'), {
        extra: {
          objectTypeId,
          objectId,
          inputMode,
          propertyName: property.name
        }
      });
    } else if (inputMode !== EDIT_INPUT_MODE && objectId) {
      devLogger.warn({
        message: `propertyinputv2-objectid-nonedit-canary: received an invalid objectId/inputMode (objectId is not expected when inputMode != "edit"): objectTypeId=${objectTypeId}, objectId=${objectId}, inputMode=${inputMode}, propertyName=${property.name}`,
        key: 'propertyinputv2-objectid-nonedit-canary'
      });
      Metrics.counter('propertyinputv2-objectid-nonedit-canary').increment();
      Raven.captureException(new Error('propertyinputv2 objectid nonedit canary'), {
        extra: {
          objectTypeId,
          objectId,
          inputMode,
          propertyName: property.name
        }
      });
    }
  }, [objectTypeId, objectId, inputMode, property.name]);
};
const useDataSensitivityCanary = ({
  objectTypeId,
  property
}) => {
  useEffect(() => {
    if (property.dataSensitivity === undefined) {
      devLogger.warn({
        message: `propertyinputv2-data-sensitivity-canary: received a property without dataSensitivity: objectTypeId=${objectTypeId}, propertyName=${property.name}`,
        key: 'propertyinputv2-data-sensitivity-canary'
      });
      Metrics.counter('propertyinputv2-data-sensitivity-canary').increment();
      // sam swindell July 15, 2024 - turned off to stop the flood until we have
      // fixed some known cases. Worried about COGS when sending too many sentries!
      // Raven.captureException(
      //   new Error('propertyinputv2 dataSensitivity canary'),
      //   {
      //     extra: {
      //       objectTypeId,
      //       propertyName: property.name,
      //     },
      //   }
      // );
    }
  }, [objectTypeId, property.dataSensitivity, property.name]);
};
const PropertyInputV2Wrapped = ({
  autoFocus,
  className,
  disabled,
  disableValidation = false,
  enableMultipleEmailInput = false,
  enablePopovers = true,
  enableCheckboxInput = false,
  enablePhoneNumberInput = true,
  enableConditionalPropertiesPanel = false,
  conditionalPropertyRules = undefined,
  FormControl: FormControlComponent,
  formControlProps = {},
  inputMode = EDIT_INPUT_MODE,
  inputProps = {},
  inputRef,
  onBlur,
  onChange,
  onFocus,
  onKeyUp,
  onTracking,
  onValidationChange = emptyFunction,
  popoverProps,
  readOnly,
  use = 'default',
  validationSettings,
  showAutoSaveText
}) => {
  const accessLevel = usePropertyAccessLevel();
  const propertyDefinition = usePropertyDefinition();
  const propertyValue = usePropertyValue();
  const internalValue = useInternalPropertyValue();
  const actions = useActionsForProperty();
  const objectId = useObjectId();
  const objectType = useObjectType();
  const objectTypeId = useObjectTypeId();
  const {
    getAdditionalPropertyValue
  } = useContext(ObjectContext);
  const {
    conditionalPropertiesPanel,
    maybeOpenConditionalPropertiesPanel
  } = useConditionalPropertiesPanel({
    enableConditionalPropertiesPanel,
    conditionalPropertyRules,
    property: propertyDefinition,
    onChange,
    objectId,
    objectTypeId,
    getAdditionalPropertyValue,
    showAutoSaveText,
    value: propertyValue,
    standaloneUsage: false
  });
  const isPropertyReadOnly = useIsPropertyReadOnly(objectTypeId, propertyDefinition, accessLevel, readOnly);
  const InputComponent = usePropertyInputComponent({
    isPropertyReadOnly,
    enableMultipleEmailInput,
    enablePopovers,
    enableCheckboxInput,
    enablePhoneNumberInput
  });
  const shouldHideEditIcon = useHideFloatingEditIcon();

  // HACK: pass on context values as props to support legacy inputs
  const legacyProps = {
    accessLevel: accessLevel || undefined,
    actions,
    internalValue,
    property: propertyDefinition,
    subjectId: objectId,
    objectType,
    objectTypeId,
    value: propertyValue
  };
  const isFileProperty = isFile(propertyDefinition);
  const shouldRunValidation = !disabled && !isPropertyReadOnly && !disableValidation &&
  // File properties cannot have custom validation rules.
  // Also, PropertyInputFilesV2 handles it's own validation using this framework.
  // Disabling validation from usePropertyValidation avoids race conditions.
  !isFileProperty;
  const {
    onChangeSingle,
    onChangeMultiple,
    validationState
  } = usePropertyValidation({
    disableValidation: !shouldRunValidation,
    objectId,
    objectTypeId,
    onChange: maybeOpenConditionalPropertiesPanel,
    onValidationChange,
    propertyDefinition,
    propertyValue,
    validationSettings
  });
  const {
    invalid,
    message
  } = validationState;
  const isHidingHighlySensitiveValue = useIsHidingHighlySensitiveValue();
  if (accessLevel !== HIDDEN && isHidingHighlySensitiveValue) {
    return /*#__PURE__*/_jsx(RequirablePropertyInputHighlySensitive, {
      className: className,
      use: use,
      formControlProps: formControlProps,
      inputProps: inputProps
    });
  }
  if (!InputComponent) {
    return null;
  }
  const isStatic = accessLevel === HIDDEN || isFileProperty;
  const validationError = shouldRunValidation && invalid;
  const error = !!formControlProps.error || validationError ? true : formControlProps.error;
  const validationMessage = formControlProps.validationMessage || (shouldRunValidation ? message : undefined);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FormControl, Object.assign({
      Component: FormControlComponent
    }, formControlProps, {
      error: error,
      validationMessage: validationMessage,
      readOnly: !!formControlProps.readOnly || isPropertyReadOnly || disabled,
      hideEditIcon: isPropertyReadOnly || disabled || accessLevel === HIDDEN || shouldHideEditIcon,
      isStatic: isStatic,
      formControlUse: use,
      children: /*#__PURE__*/_jsx(InputWithFloatingFormControlFocus, Object.assign({
        InputComponent: InputComponent
      }, legacyProps, inputProps, {
        autoFocus: autoFocus,
        className: className,
        disabled: disabled,
        inputMode: inputMode,
        inputRef: inputRef,
        onBlur: onBlur
        // These change handlers are provided by usePropertyValidation
        // so it can intercept onChange events and trigger validation
        ,
        onChange: onChangeSingle,
        onChangeMultiple: onChangeMultiple,
        onFocus: onFocus,
        onKeyUp: onKeyUp,
        onTracking: onTracking,
        onValidationChange: onValidationChange,
        readOnly: isPropertyReadOnly,
        formControlUse: use,
        showAutoSaveText: showAutoSaveText,
        enablePopovers: enablePopovers,
        popoverProps: popoverProps,
        error: error,
        validationMessage: validationMessage,
        disableValidation: disableValidation
      }))
    })), conditionalPropertiesPanel]
  });
};
const DefaultActions = {};
const PropertyInputV2 = props => {
  const {
      accessLevel: accessLevelOverride,
      actions = DefaultActions,
      objectId,
      objectTypeId,
      property,
      value,
      enableConditionalPropertyOptions,
      formControlProps,
      // @ts-expect-error TS confused by union, its OK if this pulls undefined
      getAdditionalPropertyValue,
      // @ts-expect-error TS confused by union, its OK if this pulls undefined
      getSavedValue,
      // @ts-expect-error TS confused by union, its OK if this pulls undefined
      getDraftValue
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const normalizedProperty = useMemo(() => normalizePropertyDefinition(property), [property]);
  const getAdditionalPropertyValueProxy = useGetAdditionalPropertyValueProxy({
    getAdditionalPropertyValue,
    getDraftValue,
    getSavedValue,
    objectTypeId,
    propertyName: property.name,
    componentName: 'propertyinputv2'
  });
  useValueCanary(props);
  useObjectIdCanary(props);
  useDataSensitivityCanary(props);
  const {
    data: conditionalPropertyOptionsData
  } = useConditionalPropertyOptions({
    propertyDefinition: normalizedProperty,
    enableConditionalPropertyOptions,
    objectTypeId,
    objectId,
    getAdditionalPropertyValue: getAdditionalPropertyValueProxy,
    value
  });
  const formControlPropsWithConditionalPropertyOptions = useMemo(() => {
    if (!conditionalPropertyOptionsData) {
      return formControlProps;
    }

    // We want to show any validation errors from the conditional property options.
    // But, if the host app passed us an error, we defer to that instead.
    const {
      error,
      validationMessage
    } = conditionalPropertyOptionsData;
    if (formControlProps && formControlProps.error || !error) {
      return formControlProps;
    }
    return Object.assign({}, formControlProps, {
      error,
      validationMessage
    });
  }, [conditionalPropertyOptionsData, formControlProps]);
  const actionsContextValue = useMemo(() => ({
    actions
  }), [actions]);
  const objectContextValue = useMemo(() => ({
    getAdditionalPropertyValue: getAdditionalPropertyValueProxy,
    objectId
  }), [getAdditionalPropertyValueProxy, objectId]);
  return /*#__PURE__*/_jsx(ObjectContext.Provider, {
    value: objectContextValue,
    children: /*#__PURE__*/_jsx(PropertyContextProvider, {
      accessLevelOverride: accessLevelOverride,
      objectTypeId: objectTypeId,
      value: value,
      property: conditionalPropertyOptionsData ? conditionalPropertyOptionsData.propertyDefinition : normalizedProperty,
      children: /*#__PURE__*/_jsx(ActionsContext.Provider, {
        value: actionsContextValue,
        children: /*#__PURE__*/_jsx(PropertyInputV2Wrapped, Object.assign({}, rest, {
          formControlProps: formControlPropsWithConditionalPropertyOptions
        }))
      })
    })
  });
};
export default PropertyInputV2;